import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signUpFan } from '../auth/fan-auth';
import { useI18n } from '../i18n';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { AuthShell } from './auth/AuthShell';
import { friendlyFanAuthError } from './auth/errorMessages';
import { PasswordRuleHints, PasswordMismatchHint, passwordMeetsPolicy } from './auth/PasswordRules';
import {
  errorBox,
  inputClass,
  labelClass,
  primaryButton,
} from './auth/formStyles';

/**
 * Fan-side self-service signup. After Cognito accepts the signup,
 * Cognito mails a verification link that lands on /confirm (the
 * existing auto-confirm page). We forward the user to /confirm-account
 * with their email pre-filled so they can also paste the code
 * manually if the link doesn't work.
 */
export function Signup() {
  const { t } = useI18n();
  useDocumentTitle(t('auth.signup.title'));
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pwAllOk = passwordMeetsPolicy(password);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email || !password) return;
    if (!pwAllOk) {
      setError(t('auth.errors.passwordTooShort'));
      return;
    }
    if (password !== passwordConfirm) {
      setError(t('auth.errors.passwordMismatch'));
      return;
    }
    if (!agreed) {
      setError(t('auth.errors.mustAgreeTerms'));
      return;
    }
    setPending(true);
    try {
      await signUpFan(email.trim(), password, displayName.trim() || undefined);
      nav(`/confirm-account?email=${encodeURIComponent(email.trim())}`);
    } catch (err) {
      setError(friendlyFanAuthError(err, t));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthShell
      kicker={t('auth.signup.kicker')}
      title={t('auth.signup.title')}
      subtitle={t('auth.signup.subtitle')}
      footer={
        <p className="text-center text-[11px] text-slate-500">
          {t('auth.signup.haveAccount')}{' '}
          <Link to="/login" className="font-medium text-violet-300 underline hover:text-violet-200">
            {t('auth.signup.loginLink')}
          </Link>
        </p>
      }
    >
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label htmlFor="email" className={labelClass}>{t('auth.signup.emailLabel')}</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.signup.emailPlaceholder')}
            autoComplete="email"
            autoFocus
            required
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="pw" className={labelClass}>{t('auth.signup.passwordLabel')}</label>
          <input
            id="pw"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.signup.passwordPlaceholder')}
            autoComplete="new-password"
            required
            aria-describedby="pw-rules"
            className={inputClass}
          />
          <PasswordRuleHints password={password} id="pw-rules" />
        </div>
        <div>
          <label htmlFor="pwc" className={labelClass}>{t('auth.signup.passwordConfirmLabel')}</label>
          <input
            id="pwc"
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            placeholder={t('auth.signup.passwordConfirmPlaceholder')}
            autoComplete="new-password"
            required
            className={inputClass}
          />
          <PasswordMismatchHint password={password} confirm={passwordConfirm} />
        </div>
        <div>
          <label htmlFor="dn" className={labelClass}>{t('auth.signup.displayNameLabel')}</label>
          <input
            id="dn"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('auth.signup.displayNamePlaceholder')}
            autoComplete="nickname"
            className={inputClass}
          />
        </div>
        <label className="flex items-start gap-2 text-[11px] leading-snug text-slate-400">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 rounded border-slate-700 bg-slate-950 text-violet-500 focus:ring-violet-500/50"
          />
          {/* Inline links to /terms + /privacy. target=_blank so signup
              form state isn't lost when reading the legal text. */}
          <span>
            我已閱讀並同意{' '}
            <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-violet-300 underline hover:text-violet-200">
              服務條款
            </Link>{' '}
            與{' '}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-violet-300 underline hover:text-violet-200">
              隱私權政策
            </Link>
          </span>
        </label>
        <button type="submit" disabled={pending} className={primaryButton}>
          {pending ? t('auth.signup.submitting') : t('auth.signup.submit')}
        </button>
      </form>
      {error && <p role="alert" className={errorBox}>{error}</p>}
    </AuthShell>
  );
}
